<template>
  <component :is="layout">
    <div class="min-h-[calc(100vh-4rem)] bg-linear-to-b from-slate-50 to-white">
      <!-- Cinematic Map Hero Section: the map is a background that fills the hero;
           the hero is sized by the price card (in flow) with a breakpoint floor, so the
           card can never rise under the fixed navbar nor sink into the pulled-up content. -->
      <div class="relative min-h-[400px] sm:min-h-[480px] lg:min-h-[520px]">
        <!-- Back Button - Floating over map -->
        <div class="absolute top-4 left-4 z-20">
          <Link
            href="/quick-valuation"
            class="inline-flex items-center gap-2 px-3 py-2 text-sm text-slate-600 bg-white/90 backdrop-blur-xs rounded-lg shadow-xs hover:bg-white hover:shadow-md transition-all"
          >
            <span class="pi pi-arrow-left text-xs"></span>
            {{ $t('messages.quickValuation.newValuation') }}
          </Link>
        </div>

        <!-- View Details Button - Floating over map -->
        <div v-if="showCalculationLink && layoutName === 'FocusedLayout'">
          <Button
            class="absolute top-4 right-4 z-20 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm text-slate-600 bg-white/90 backdrop-blur-xs rounded-lg shadow-xs hover:bg-white hover:shadow-md transition-all"
            @click="showRegionUpgradeDialog = true"
          >
            <span class="pi pi-external-link text-xs"></span>
            {{ $t('messages.quickValuation.viewDetails') }}
          </Button>
        </div>
        <Link
          v-else-if="showCalculationLink"
          :href="`/evaluation/${evaluation.id}`"
          class="absolute top-4 right-4 z-20 hidden sm:inline-flex items-center gap-2 px-3 py-2 text-sm text-slate-600 bg-white/90 backdrop-blur-xs rounded-lg shadow-xs hover:bg-white hover:shadow-md transition-all"
        >
          <span class="pi pi-external-link text-xs"></span>
          {{ $t('messages.quickValuation.viewDetails') }}
        </Link>

        <!-- Map Background -->
        <MapBackground
          v-if="evaluation.coordinates"
          :coordinates="evaluation.coordinates"
          :zoom="16"
        />

        <!-- Fallback gradient when no coordinates -->
        <div
          v-else
          class="absolute inset-0 bg-linear-to-b from-slate-100 via-slate-50 to-slate-50"
        ></div>

        <!-- Price Card - floating over the map, below the corner controls (top-4 + 36px);
             pb-40 keeps a 48px map strip above the main content's -mt-28 pull-up. -->
        <div class="relative z-10 px-4 pt-16 pb-40">
          <div class="max-w-2xl mx-auto">
            <div class="price-card rounded-3xl overflow-hidden">
              <Deferred data="prediction">
                <template #default>
                  <div v-if="prediction.available" class="p-6 sm:p-8 text-center animate-fade-in">
                    <!-- RED-262 gates v1: a valuer / out-of-scope answer has no number at all;
                         GatesSummary below carries the whole card. -->
                    <template v-if="!gatesNoValue">
                      <p class="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                        {{ $t('messages.quickValuation.resultTitle') }}
                      </p>
                      <div data-testid="qv-result-price" class="text-4xl sm:text-5xl md:text-6xl font-light text-slate-800 mb-3 animate-count-up tabular-nums">
                        {{ formatPrice(displayPrice) }} <span class="text-2xl sm:text-3xl text-slate-400">€</span>
                      </div>
                      <div class="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm text-slate-500">
                        <div v-if="pricePerSqm" class="flex items-center gap-1">
                          <span class="font-medium text-slate-700 tabular-nums">{{ formatPrice(pricePerSqm) }}</span>
                          <span>{{ $t('messages.quickValuation.pricePerSqm') }}</span>
                        </div>
                        <!-- A gates headline is the model's own price, not the comparables'. -->
                        <div v-if="!gatesDecides && prediction.similar_properties?.length" class="flex items-center gap-1.5">
                          <span class="w-1.5 h-1.5 rounded-full bg-blue-400"></span>
                          <span>
                            {{ $t('messages.quickValuation.basedOn', { count: prediction.similar_properties.length }) }}
                          </span>
                        </div>
                      </div>
                    </template>
                    <!-- RED-262 gates v1 (DEV, flag `gates-v1` on the owner): the grade and the
                         ranges under the direct price, or the valuer note in its place. -->
                    <GatesSummary
                      v-if="gatesDecides"
                      :class="{ 'mt-4': !gatesNoValue }"
                      :gates="prediction.gates"
                    />
                    <!-- RED-290 tightness badge: flag-gated, nothing reserved when absent.
                         RED-356: `qv-statistical-report` OFF renders LIVE's badge and
                         direct banner unchanged (Toomas 02.10 15:40). ON, the chip is
                         the server's `tightness_grade` (Jaan 13.10), which every
                         direct result carries as lai even with no tightness (RED-262).
                         A gates v1 grade replaces it. -->
                    <template v-if="showTightness && !gatesDecides">
                      <UncertaintyBadge
                        v-if="statisticalReportEnabled && (prediction?.tightness || prediction?.tightness_grade)"
                        class="mt-3"
                        :tightness="prediction.tightness ?? null"
                        :grade="prediction.tightness_grade ?? null"
                        :scroll-target="prediction?.model_id ? 'model-quality' : null"
                      />
                      <UncertaintyBadgeLive
                        v-else-if="!statisticalReportEnabled && prediction?.tightness"
                        class="mt-3"
                        :tightness="prediction.tightness"
                        :scroll-target="prediction?.model_id ? 'model-quality' : null"
                      />
                    </template>
                    <!-- The direct model's confidence line. RED-262 gates v1 on a direct route:
                         the grade replaces it (its words are the multi-regime model's, not the
                         locked-scale price's), as it replaces the badge above. -->
                    <template v-if="isDirect && !gatesDecides">
                      <DirectConfidenceBanner
                        v-if="statisticalReportEnabled"
                        class="mt-4"
                        :confidence="prediction.confidence"
                        :low-confidence-flag="Boolean(prediction.low_confidence_flag)"
                        :gate="prediction.gate ?? null"
                        :sealed="prediction.sealed ?? null"
                      />
                      <DirectConfidenceBannerLive
                        v-else
                        class="mt-4"
                        :confidence="prediction.confidence"
                        :quality="prediction.quality"
                        :low-confidence-flag="Boolean(prediction.low_confidence_flag)"
                        :gate="prediction.gate ?? null"
                      />
                    </template>
                    <!-- RED-262 last-resort median (flag qv-fallback-median): the price is the
                         settlement's median €/m² × the flat's area. One calm sentence (draft
                         wording for Toomas and Jaan); the precision word is the badge's madal. -->
                    <Message
                      v-if="isFallbackMedian"
                      data-testid="qv-fallback-median-notice"
                      severity="secondary"
                      :closable="false"
                      class="mt-4 text-left"
                    >
                      {{ $t('messages.quickValuation.fallbackMedian.notice') }}
                    </Message>
                    <!-- RED-262 gates v1 asked, but no ranges came back: today's result
                         under the price and its badge, then one quiet line. -->
                    <p v-if="gatesUnavailable" data-testid="qv-gates-unavailable" class="mt-3 text-xs text-slate-400">
                      {{ $t('messages.quickValuation.gatesV1.unavailable') }}
                    </p>
                    <!-- RED-356 (Toomas 19.10): no "Masinõppe hinnang on abivahend, …" line
                         under the price (RED-262 #367 added it; removed, nothing in its place). -->
                    <div v-if="canExport" class="mt-5 flex justify-center">
                      <Button
                        v-tooltip.top="!quickValuationTemplates.length
                          ? $t('messages.quickValuation.shortReport.noTemplates')
                          : null"
                        data-testid="qv-result-open-short-report"
                        :label="$t('messages.quickValuation.shortReport.open')"
                        icon="pi pi-file-word"
                        :disabled="!quickValuationTemplates.length"
                        @click="showShortReportDialog = true"
                      />
                    </div>
                  </div>
                  <div v-else class="p-8 text-center">
                    <span class="pi pi-exclamation-circle text-3xl text-amber-500 mb-3 block"></span>
                    <p class="text-base text-slate-600">
                      {{ $t('messages.quickValuation.predictionUnavailable.title') }}
                    </p>
                    <p class="text-sm text-slate-500 mt-2">
                      {{ prediction.error || $t('messages.quickValuation.predictionUnavailable.subtitle') }}
                    </p>
                  </div>
                </template>
                <template #fallback>
                  <div class="p-8 text-center">
                    <ProgressSpinner style="width: 40px; height: 40px" />
                    <p class="mt-3 text-sm text-slate-500">Arvutan hinda...</p>
                  </div>
                </template>
              </Deferred>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content - Offset to account for floating price card -->
      <div class="relative z-20 max-w-4xl mx-auto px-4 pb-14 -mt-16 sm:-mt-28">
        <!-- Property Address Info -->
        <div class="text-center mb-8">
          <h1 data-testid="qv-result-address" class="text-2xl sm:text-3xl font-normal text-slate-800 tracking-tight mb-1">
            {{ predictionAddress.address || 'Korter' }}
          </h1>
          <p v-if="predictionAddress.addressSub" class="text-slate-500">
            {{ predictionAddress.addressSub }}
          </p>
          <p v-if="evaluation.cadaster_id" class="text-sm text-slate-400 mt-1">
            {{ evaluation.cadaster_id }}
          </p>
        </div>

        <!-- Key Facts -->
        <div v-if="keyFacts.length" class="mb-6">
          <div class="bg-white rounded-2xl border border-slate-100 shadow-xs px-4 py-3">
            <div class="flex flex-wrap items-baseline gap-x-6 gap-y-2">
              <div
                v-for="fact in keyFacts"
                :key="fact.key"
                class="flex items-baseline gap-1.5"
              >
                <span class="text-xs uppercase tracking-wide text-slate-400">
                  {{ $t(`messages.quickValuation.${fact.key}`) }}
                </span>
                <span class="text-sm font-medium text-slate-800">{{ fact.value }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Comparable Transactions Table. RED-262 gates v1: none under a withheld price
             (valuer / out of scope), as the sale prices would stand in for it. -->
        <Deferred data="prediction">
          <template #default>
            <div v-if="prediction.available && prediction.similar_properties?.length && !gatesNoValue" class="mb-6" data-testid="qv-result-comparables">
              <div class="bg-white rounded-2xl shadow-xs border border-slate-100 overflow-hidden">
                <div class="p-6 border-b border-slate-100">
                  <h2 class="text-lg font-medium text-slate-800">Sarnased tehingud</h2>
                </div>
                <div class="overflow-x-auto">
                  <table class="w-full">
                    <thead class="bg-slate-50">
                      <tr>
                        <th class="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Aadress
                        </th>
                        <th class="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Pind
                        </th>
                        <th class="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Hind
                        </th>
                        <th class="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                          €/m²
                        </th>
                        <th class="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Kuupäev
                        </th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-100">
                      <tr
                        v-for="(property, index) in displayComparables"
                        :key="property.id"
                        class="hover:bg-slate-50 transition-colors"
                        :class="{ 'animate-slide-in': true }"
                        :style="{ animationDelay: `${index * 100}ms` }"
                      >
                        <td class="px-6 py-4">
                          <div class="font-medium text-slate-800 truncate max-w-xs" :title="property.addressFull">
                            {{ property.address }}
                          </div>
                          <div v-if="property.addressSub" class="text-xs text-slate-500 truncate max-w-xs" :title="property.addressFull">
                            {{ property.addressSub }}
                          </div>
                        </td>
                        <td class="px-6 py-4 text-right text-slate-600">
                          {{ property.area ?? '-' }}
                          <span class="text-slate-400">m²</span>
                        </td>
                        <td class="px-6 py-4 text-right font-medium text-slate-800">
                          {{ formatPrice(property.price) }}
                        </td>
                        <td class="px-6 py-4 text-right text-slate-600">
                          {{ formatPrice(property.sqmPrice) }}
                        </td>
                        <td class="px-6 py-4 text-center text-slate-500 text-sm">
                          {{ formatDate(property.date) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div
                  v-if="prediction.similar_properties.length > 5"
                  class="px-6 py-4 border-t border-slate-100 flex items-center justify-end"
                >
                  <button
                    type="button"
                    class="text-sm font-medium text-blue-600 hover:text-blue-700"
                    @click="toggleComparables"
                  >
                    {{ showAllComparables ? $t('messages.quickValuation.showLess') : $t('messages.quickValuation.showMore') }}
                  </button>
                </div>
              </div>
            </div>
          </template>
          <template #fallback>
            <div class="mb-6">
              <div class="bg-white rounded-2xl shadow-xs border border-slate-100 p-6">
                <Skeleton width="100%" height="200px" />
              </div>
            </div>
          </template>
        </Deferred>

        <!-- Adjustment Factors (none under a withheld gates v1 price) -->
        <Deferred data="prediction">
          <template #default>
            <div v-if="prediction.available && hasAdjustments && !gatesNoValue" class="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="qv-result-factors">
              <!-- Positive Factors -->
              <div v-if="positiveFactors.length" class="bg-white rounded-2xl shadow-xs border border-slate-100 p-6">
                <div class="flex items-center gap-2 mb-4">
                  <span class="pi pi-arrow-up-right text-emerald-500"></span>
                  <h3 class="font-medium text-slate-800">{{ $t('messages.quickValuation.positiveFactors') }}</h3>
                </div>
                <ul class="space-y-3">
                  <li
                    v-for="(factor, index) in positiveFactors.slice(0, 5)"
                    :key="index"
                    class="flex items-start gap-3"
                  >
                    <span class="w-2 h-2 rounded-full bg-emerald-400 mt-2 shrink-0"></span>
                    <span class="text-slate-600">{{ factor.label || factor }}</span>
                  </li>
                </ul>
              </div>

              <!-- Negative Factors -->
              <div v-if="negativeFactors.length" class="bg-white rounded-2xl shadow-xs border border-slate-100 p-6">
                <div class="flex items-center gap-2 mb-4">
                  <span class="pi pi-arrow-down-right text-red-500"></span>
                  <h3 class="font-medium text-slate-800">{{ $t('messages.quickValuation.negativeFactors') }}</h3>
                </div>
                <ul class="space-y-3">
                  <li
                    v-for="(factor, index) in negativeFactors.slice(0, 5)"
                    :key="index"
                    class="flex items-start gap-3"
                  >
                    <span class="w-2 h-2 rounded-full bg-red-400 mt-2 shrink-0"></span>
                    <span class="text-slate-600">{{ factor.label || factor }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </template>
          <template #fallback>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="bg-white rounded-2xl shadow-xs border border-slate-100 p-6">
                <Skeleton width="100%" height="150px" />
              </div>
              <div class="bg-white rounded-2xl shadow-xs border border-slate-100 p-6">
                <Skeleton width="100%" height="150px" />
              </div>
            </div>
          </template>
        </Deferred>

        <!-- Mudeli kvaliteedi mõõdikud (RED-196 + RED-197 item 5).
             The outer `v-if` skips the section entirely for legacy QV rows
             whose prediction predates the model_id capture (no fetch attempt,
             no skeleton flash). RED-355 B7: the server's `evidence` decides
             what it prints, measured numbers with their basis in words or the
             B6 pending line; it is never blank. RED-356: with
             `qv-statistical-report` OFF the server sends LIVE's dossier (null
             when unavailable) and LIVE's section renders it unchanged.
             RED-262 gates v1: none under a withheld price (valuer / out of scope),
             as there is no automatic value for it to describe. -->
        <template v-if="prediction?.model_id && !gatesNoValue">
          <Deferred data="modelQualityMetrics">
            <template #default>
              <template v-if="statisticalReportEnabled">
                <ModelQualitySection
                  v-if="modelQualityMetrics?.evidence"
                  :model-quality-metrics="modelQualityMetrics"
                />
              </template>
              <ModelQualitySectionLive
                v-else-if="modelQualityMetrics"
                :model-quality-metrics="modelQualityMetrics"
              />
            </template>
            <template #fallback>
              <div class="bg-white rounded-2xl shadow-xs border border-slate-100 p-6 mt-6">
                <Skeleton width="100%" height="160px" />
              </div>
            </template>
          </Deferred>
        </template>

        <!-- RED-355: EVS 875-13 annex C ESG table, as the lühiaruanne prints it. -->
        <QvEsgAnnexCSection v-if="esgAnnexCEnabled" />

        <!-- Action Buttons (Mobile) -->
        <div class="sm:hidden flex flex-col gap-3 mt-6">
          <Button
            v-if="prediction?.available && canExport"
            data-testid="qv-result-open-short-report-mobile"
            :label="$t('messages.quickValuation.shortReport.open')"
            icon="pi pi-file-word"
            class="w-full"
            :disabled="!quickValuationTemplates.length"
            @click="showShortReportDialog = true"
          />
          <Link
            v-if="showCalculationLink"
            :href="`/evaluation/${evaluation.id}`"
            class="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors"
          >
            <span class="pi pi-external-link"></span>
            {{ $t('messages.quickValuation.viewDetails') }}
          </Link>
          <Link
            href="/quick-valuation"
            class="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors"
          >
            <span class="pi pi-plus"></span>
            {{ $t('messages.quickValuation.newValuation') }}
          </Link>
        </div>
      </div>
    </div>
    <!-- Region Upgrade Dialog -->
    <RegionUpgradeDialog
      v-if="layoutName === 'FocusedLayout'"
      v-model:visible="showRegionUpgradeDialog"
      :region-name="lockedRegionName"
    />
    <!-- Off-screen map capture host (RED-212): owns hidden maps + warm-capture
         lifecycle so QuickValuationHistory.vue can reuse the same code path. -->
    <QvMapCaptureHost
      v-if="canExport"
      ref="captureHostRef"
      :evaluation-id="evaluation.id"
      :ehr-data="ehrData"
      :land-board-bbox="landBoard?.bbox ?? null"
      warm-capture
    />
    <!-- Short Report (Lühiaruanne) Dialog -->
    <ShortReportDialog
      v-if="canExport"
      v-model:visible="showShortReportDialog"
      :evaluation="shortReportEvaluation"
      :templates="quickValuationTemplates"
      :capture-maps="captureMaps"
      :has-existing-land-registry="hasExistingLandRegistry"
      :existing-kr-date="existingKrDate"
      :cadaster-id="evaluation.cadaster_id"
    />
  </component>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Link, usePage } from '@inertiajs/vue3'
import { Deferred } from '@inertiajs/vue3'
import AppLayout from '@/Layouts/AppLayout.vue'
import FocusedLayout from '@/Layouts/FocusedLayout.vue'
import DirectConfidenceBanner from '@/components/quick-valuation/DirectConfidenceBanner.vue'
import DirectConfidenceBannerLive from '@/components/quick-valuation/DirectConfidenceBannerLive.vue'
import GatesSummary from '@/components/quick-valuation/GatesSummary.vue'
import MapBackground from '@/components/quick-valuation/MapBackground.vue'
import ModelQualitySection from '@/components/quick-valuation/ModelQualitySection.vue'
import ModelQualitySectionLive from '@/components/quick-valuation/ModelQualitySectionLive.vue'
import QvEsgAnnexCSection from '@/components/quick-valuation/QvEsgAnnexCSection.vue'
import QvMapCaptureHost from '@/components/quick-valuation/QvMapCaptureHost.vue'
import RegionUpgradeDialog from '@/components/quick-valuation/RegionUpgradeDialog.vue'
import ShortReportDialog from '@/components/quick-valuation/ShortReportDialog.vue'
import UncertaintyBadge from '@/components/quick-valuation/UncertaintyBadge.vue'
import UncertaintyBadgeLive from '@/components/quick-valuation/UncertaintyBadgeLive.vue'
import { useFeature } from '@/composables/useFeature'
import { useQuickValuationHydration } from '@/composables/useQuickValuationHydration'
import { resolveBuildingPart } from '@/services/buildingPartResolver'

const { t } = useI18n()
const pageProps = computed(() => usePage().props)
const layoutName = computed(() => pageProps.value?.layout)
const layout = computed(() => layoutName.value === 'FocusedLayout' ? FocusedLayout : AppLayout)
const showRegionUpgradeDialog = ref(false)
const showShortReportDialog = ref(false)
const lockedRegionName = ref(null)
const can = computed(() => pageProps.value?.auth?.can)
const captureHostRef = ref(null)

const captureMaps = (onProgress) =>
  captureHostRef.value?.captureAll
    ? captureHostRef.value.captureAll(onProgress)
    : Promise.resolve({})

const props = defineProps({
  evaluation: {
    type: Object,
    required: true,
  },
  ehrData: {
    type: Object,
    default: () => ({}),
  },
  prediction: {
    type: Object,
    default: () => ({
      available: false,
      prediction: null,
      similar_properties: [],
      grouped_differences_summary: [],
    }),
  },
  building_part_id: {
    type: String,
    default: null,
  },
  landBoard: {
    type: Object,
    default: null,
  },
  needsRefresh: {
    type: Boolean,
    default: false,
  },
  quickValuationTemplates: {
    type: Array,
    default: () => [],
  },
  /**
   * Mudeli kvaliteedi mõõdikud — full payload from REdata's
   * GET /v2/models/{id}/quality-metrics. Deferred by the controller; arrives
   * after initial paint. Null when the valuation didn't capture a model_id
   * (older rows) or the API call failed.
   */
  modelQualityMetrics: {
    type: Object,
    default: null,
  },
})

// Populate the shared stores so the Detail map components + ESG maps mounted
// offscreen by QvHiddenMaps / QvHiddenEsgMaps — and the transaction store's
// `comparisonTableExport` computed — all find the same state the Detail page
// would populate: ehr data, landBoard, masterData, prediction, building part.
const { hydrate, reset } = useQuickValuationHydration()

// Track the bbox we last fetched ESG data for. The hydration cascade fires
// fetchAllEsgData unconditionally on every call; Inertia re-syncs would
// otherwise retrigger the 8-endpoint burst.
let lastEsgFetchBboxKey = null

// Track which evaluation we last hydrated. Inertia preserves this component
// across QV → QV navigations (same component, new props), so without this
// the previous evaluation's selectionTableRows + ESG state leak into the next
// run and the Kohandustabel ends up with 6 rows / Mall export carries stale
// auto-answers (RED-214).
let lastHydratedEvaluationId = null

const syncDetailStoreFromProps = async () => {
  const bbox = props.landBoard?.bbox
  const bboxKey = Array.isArray(bbox) && bbox.length ? JSON.stringify(bbox) : null
  const skipEsgFetch = bboxKey !== null && bboxKey === lastEsgFetchBboxKey

  const newEvaluationId = props.evaluation?.id ?? null
  if (
    lastHydratedEvaluationId !== null &&
    newEvaluationId !== null &&
    lastHydratedEvaluationId !== newEvaluationId
  ) {
    reset()
    lastEsgFetchBboxKey = null
  }

  await hydrate(
    {
      evaluation: props.evaluation,
      ehrData: props.ehrData,
      landBoard: props.landBoard,
      prediction: props.prediction,
      buildingPartId: props.building_part_id,
    },
    { skipEsgFetch },
  )

  if (!skipEsgFetch && bboxKey) {
    lastEsgFetchBboxKey = bboxKey
  }
  if (newEvaluationId !== null) {
    lastHydratedEvaluationId = newEvaluationId
  }
}

// Populate during setup (synchronous) so child components mounted below —
// QvHiddenMaps / QvHiddenEsgMaps → MapComponent — find ehrGeometry + landBoard
// already present when their onMounted fires. useMicroMacroDetailMap.js:77 bails
// out immediately if ehrGeometry is null at mount time, hence the setup-time write.
syncDetailStoreFromProps()

// Inertia partial reloads can replace props after mount (e.g. deferred prediction
// returning). Re-sync so the stores don't go stale.
watch(() => [props.landBoard, props.ehrData, props.prediction], syncDetailStoreFromProps, { deep: false })

onBeforeUnmount(() => {
  reset()
  lastEsgFetchBboxKey = null
  lastHydratedEvaluationId = null
})

const shortReportEvaluation = computed(() => ({
  ...props.evaluation,
  prediction: props.prediction,
}))

const existingKrDate = computed(() => {
  return props.evaluation?.assessment_data?.kinnistusraamat?.itehtud_kp ?? null
})

const hasExistingLandRegistry = computed(() => Boolean(existingKrDate.value))

// Computed
const selectedBuildingPart = computed(() =>
  // Shared adsOid-first resolver (no first-element fallback) — the same one the
  // export hydration uses, so the downloaded report's subject column always
  // matches the apartment shown here (eval 6139 regression).
  resolveBuildingPart(props.ehrData?.livingRoom, props.building_part_id),
)

/**
 * The displayed price ("Korteri hinnanguline väärtus"). Read from the
 * persisted `display_price` field — computed server-side at create time via
 * WeightedAveragePriceCalculator (Σ adjusted_price × weight per comparable),
 * matching the formula used across the full Evaluation view. The same value
 * powers the history list, so both views always agree.
 *
 * Falls back to `prediction.prediction` for legacy rows that haven't been
 * backfilled yet — the same scalar history shows for those, so the two views
 * still agree.
 */
const displayPrice = computed(() => {
  return props.prediction.display_price ?? props.prediction.prediction
})

// Direct multi-regime EBM result (no comparables): banner instead of the
// comparables badge, factor cards fed from the subject's own model
// contributions. Absent key = the classic comparables shape.
const isDirect = computed(() => props.prediction?.valuation_mode === 'direct')

// RED-262 last-resort median (FallbackMedian::PATH): no comparables, no model.
// No lühiaruanne until Toomas and Jaan decide what it should say.
const isFallbackMedian = computed(() => props.prediction?.valuation_mode === 'fallback_median')

// RED-262 gates v1 (DEV): the persisted `gates` block, present only when the owner's
// request asked for it (flag `gates-v1`). Read from the row, not the viewer's flag — the
// same rule as the server's 422 (GatesV1::decidesPrice): `ok` / `out_of_scope` decided the
// price and has no lühiaruanne in v1; `unavailable` left today's comparables result.
const gates = computed(() => props.prediction?.gates ?? null)
const gatesDecides = computed(() => ['ok', 'out_of_scope'].includes(gates.value?.status))
const gatesNoValue = computed(() => gatesDecides.value && !gates.value.auto_value)
const gatesUnavailable = computed(() => gates.value?.status === 'unavailable')

// RED-290: the tightness badge is HOLD live — rendered only behind the flag.
const showTightness = useFeature('qv-uncertainty-badge')

// RED-356: behind the flag a direct result gets the lühiaruanne too (the
// server lists only the statistical-model mall for it). Flag off = v1: no
// docx export for direct results. Flag off also renders LIVE's badge, direct
// banner and "Mudeli kvaliteet" (the *Live components), so users see exactly
// what LIVE shows until Toomas approves the new ones (02.10 15:40).
const statisticalReportEnabled = useFeature('qv-statistical-report')
const esgAnnexCEnabled = useFeature('esg-annex-c')
const canExport = computed(() =>
  !isFallbackMedian.value && !gatesDecides.value && (!isDirect.value || statisticalReportEnabled.value),
)

// RED-356: "Vaata täpsemat arvutuskäiku" opens the kohandustabel; with no
// comparables there is no calculation to show, so behind the flag it goes.
// RED-262 gates v1: a valuer / out-of-scope answer withholds the price, and the
// kohandustabel would show it one click away, so the link goes too.
const showCalculationLink = computed(() => !isFallbackMedian.value && !gatesNoValue.value && (
  !statisticalReportEnabled.value || Boolean(props.prediction?.similar_properties?.length)),
)

const pricePerSqm = computed(() => {
  const price = displayPrice.value
  if (!price) return null

  // Try to get area from selected building part
  const area = selectedBuildingPart.value?.['ehitise-osa-pind']
  if (area && area > 0) {
    return Math.round(price / area)
  }

  return null
})

const keyFacts = computed(() => {
  const living = selectedBuildingPart.value
  const facts = []
  if (living?.['ehitise-osa-pind']) {
    facts.push({ key: 'factArea', value: `${living['ehitise-osa-pind']} m²` })
  }
  if (living?.['ehitise-osa-tubade-arv']) {
    facts.push({ key: 'factRooms', value: living['ehitise-osa-tubade-arv'] })
  }
  if (living?.['ehitise-osa-korrus']) {
    facts.push({ key: 'factFloor', value: living['ehitise-osa-korrus'] })
  }
  const year = props.ehrData?.general?.ehitiseValmimisaasta || props.ehrData?.general?.ehitiseValmidus || props.ehrData?.construction?.['ehitise-valmimisaasta']
  if (year) {
    facts.push({ key: 'factYear', value: year })
  }
  if (props.evaluation?.cadaster_id) {
    facts.push({ key: 'factCadastre', value: props.evaluation.cadaster_id })
  }
  return facts.slice(0, 6)
})

const positiveFactors = computed(() => {
  const grouped = props.prediction.grouped_differences_summary || []
  return grouped
    .filter(g => g.direction === 'positive' || g.impact > 0)
    .slice(0, 5)
})

const negativeFactors = computed(() => {
  const grouped = props.prediction.grouped_differences_summary || []
  return grouped
    .filter(g => g.direction === 'negative' || g.impact < 0)
    .slice(0, 5)
})

const hasAdjustments = computed(() => {
  return positiveFactors.value.length > 0 || negativeFactors.value.length > 0
})

const showAllComparables = ref(false)

const normalizedComparables = computed(() => {
  if (!props.prediction.similar_properties) return []

    return props.prediction.similar_properties.map((p) => {
    const price = p.tehingusumma ?? p.price ?? p.hind ?? null
    const area = p.korteri_pind ?? p.area ?? p.pind ?? p.hooneosa_pind ?? null
      const fullAddress = p.address ?? p.taisaadress ?? p.tais_aadress ?? p.lahiaadress ?? '-'
      const addressParts = typeof fullAddress === 'string' ? fullAddress.split(',').map(part => part.trim()).filter(Boolean) : []
      const addressSub = addressParts.length > 0 ? addressParts.slice(1, -1).join(', ') : fullAddress
      const addressMain = addressParts.length > 1 ? addressParts.slice(-1).join(', ') : ''

    return {
      id: p.property_id ?? p.id ?? Math.random().toString(36).slice(2),
        address: addressMain,
        addressSub,
        addressFull: fullAddress,
      area,
      price,
      sqmPrice: p.ruutmeetri_hind ?? p.ruutm2_hind ?? (price && area ? Math.round(price / area) : null),
      date: p.tehingukuupaev ?? p.date ?? p.kuupaev ?? null,
      raw: p,
    }
  })
})

const predictionAddress = computed(() => {
  const fullAddress = props.evaluation.address ?? props.evaluation.taisaadress ?? props.evaluation.tais_aadress ?? props.evaluation.lahiaadress ?? '-'
  const addressParts = typeof fullAddress === 'string' ? fullAddress.split(',').map(part => part.trim()).filter(Boolean) : []
  const addressSub = addressParts.length > 0 ? addressParts.slice(1, -1).join(', ') : fullAddress
  const addressMain = addressParts.length > 1 ? addressParts.slice(0, 1).join(', ') : ''
  return {
    address: addressMain,
    addressSub,
    addressFull: fullAddress,
  }
})

const displayComparables = computed(() => {
  if (showAllComparables.value) {
    return normalizedComparables.value
  }
  return normalizedComparables.value.slice(0, 5)
})

// Methods
const formatPrice = (value) => {
  if (!value) return '-'
  return new Intl.NumberFormat('et-EE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  try {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('et-EE', {
      year: 'numeric',
      month: '2-digit',
    }).format(date)
  } catch {
    return dateString
  }
}

const toggleComparables = () => {
  showAllComparables.value = !showAllComparables.value
}
</script>

<style scoped>
/* Glassmorphic price card */
.price-card {
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(255, 255, 255, 0.6) inset,
    0 1px 0 0 rgba(255, 255, 255, 0.8) inset;
  border: 1px solid rgba(148, 163, 184, 0.15);
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slide-in {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-fade-in {
  animation: fade-in 0.5s ease-out forwards;
}

.animate-slide-in {
  animation: slide-in 0.4s ease-out forwards;
  opacity: 0;
}

.animate-count-up {
  animation: fade-in 0.6s ease-out forwards;
}
</style>
